import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import * as R from 'ramda';
import { useTranslation } from 'react-i18next';

import connect from '../utils/connect';

import { Link, useNavigate } from 'react-router-dom';

import notificationActions from '../actions/notifications';
import sportsApi from '@app/utils/sportsApi';
import './TwitterRedirect.scss';

const TWITTER_PLATFORM_ID = 1;

const getAccessToken = ({ token, tokenSecret, oauthVerifier }) =>
    sportsApi
        .url('/social-account-access-tokens')
        .query({ token, tokenSecret, oauthVerifier, platform: 'X' })
        .get()
        .json();

const saveAccount = clientAuthorizedAccount =>
    sportsApi
        .url(`/clients/${clientAuthorizedAccount.clientId}/authorized-accounts`)
        .json([clientAuthorizedAccount])
        .post();

//eslint-disable-next-line react/no-multi-comp
function Loading() {
    const { t } = useTranslation('settings');
    return (
        <>
            <div className="gds-flex gds-flex--justify-around gds-flex--align-center -m-b-4">
                <div className="gds-flex__item gds-flex__item--grow-0">
                    <p className="gds-text--header-xxl -m-h-1">
                        <i className="fa fa-spinner fa-spin" />
                    </p>
                </div>
            </div>

            <div className="gds-flex gds-flex--justify-around">
                <div className="gds-flex__item gds-flex__item--grow-0">
                    <p className="gds-text--header-xs">
                        {t(
                            'clientConfiguration.social.twitterRedirect.loading',
                        )}
                    </p>
                </div>
            </div>
        </>
    );
}

//eslint-disable-next-line react/no-multi-comp
function Success() {
    const { t } = useTranslation('settings');
    return (
        <>
            <div className="gds-flex gds-flex--justify-around gds-flex--align-center -m-b-4">
                <div className="gds-flex__item gds-flex__item--grow-0">
                    <p className="gds-text--header-xxl -m-h-1">
                        <i className="fa fa-check-circle fa-grow gds-text--success" />
                    </p>
                </div>
            </div>
            <div className="gds-flex gds-flex--justify-around">
                <div className="gds-flex__item gds-flex__item--grow-0">
                    <p className="gds-text--header-xs">
                        {t(
                            'clientConfiguration.social.twitterRedirect.success',
                        )}
                    </p>
                    <p className="gds-text--body-md -text-center">
                        {t(
                            'clientConfiguration.social.twitterRedirect.redirecting',
                        )}
                    </p>
                </div>
            </div>
        </>
    );
}

//eslint-disable-next-line react/no-multi-comp
function ErrorComponent() {
    const { t } = useTranslation('settings');
    return (
        <>
            <div className="gds-flex gds-flex--justify-around gds-flex--align-center -m-b-4">
                <div className="gds-flex__item gds-flex__item--grow-0">
                    <p className="gds-text--header-xxl -m-h-1">
                        <i className="fa fa-times-circle fa-grow gds-text--danger" />
                    </p>
                </div>
            </div>
            <div className="gds-flex gds-flex--justify-around">
                <div className="gds-flex__item gds-flex__item--grow-0">
                    <p className="gds-text--header-xs">
                        {t('clientConfiguration.social.twitterRedirect.error')}
                    </p>
                </div>
            </div>
            <div className="gds-flex gds-flex--justify-around -m-t-3">
                <div className="gds-flex__item gds-flex__item--grow-0">
                    <Link to="/client-configuration/linked-social-accounts">
                        <button
                            type="button"
                            className="gds-button gds-button--md gds-button--primary"
                        >
                            {t(
                                'clientConfiguration.social.twitterRedirect.backButton',
                            )}
                        </button>
                    </Link>
                </div>
            </div>
        </>
    );
}

//eslint-disable-next-line react/no-multi-comp
function TwitterRedirect({ send: sendNotification, client }) {
    const { t } = useTranslation('settings');
    const [hadError, setError] = useState(null);
    const [wasSuccessful, setSuccess] = useState(false);
    const navigate = useNavigate();

    const getErrorMsgInternal = error => {
        try {
            const errorJSON = JSON.parse(error.text);
            const message =
                R.path(['message'], errorJSON) ||
                t('clientConfiguration.social.twitterRedirect.somethingWrong');
            return message;
        } catch (e) {
            return t(
                'clientConfiguration.social.twitterRedirect.somethingWrong',
            );
        }
    };

    useEffect(() => {
        (async () => {
            try {
                const clientId = client.id;
                const query = new URLSearchParams(window.location.search);

                const oauthToken = query.get('oauth_token');
                const oauthVerifier = query.get('oauth_verifier');

                const storedTokens = localStorage.getItem('twitter-auth');

                if (!storedTokens) {
                    setError(true);
                    return;
                }

                const { token, tokenSecret } = JSON.parse(storedTokens);

                if (oauthToken !== token) {
                    setError(true);
                }

                const res = await getAccessToken({
                    token,
                    tokenSecret,
                    oauthVerifier,
                });

                const clientAuthorizedAccount = {
                    clientId: clientId,
                    accessToken: res.accessToken,
                    tokenSecret: res.tokenSecret,
                    account: { platformId: TWITTER_PLATFORM_ID },
                };

                await saveAccount(clientAuthorizedAccount)
                    .badRequest(err => {
                        const errorMsg = getErrorMsgInternal(err);
                        sendNotification(errorMsg, { context: 'danger' });
                        throw new Error(
                            t(
                                'clientConfiguration.social.twitterRedirect.alreadyExists',
                            ),
                        );
                    })
                    .error(409, err => {
                        const errorMsg = getErrorMsgInternal(err);
                        sendNotification(errorMsg, { context: 'danger' });
                        throw new Error(
                            t(
                                'clientConfiguration.social.twitterRedirect.alreadyExists',
                            ),
                        );
                    })
                    .internalError(err => {
                        throw err;
                    })
                    .res(() => {
                        setSuccess(true);
                    });

                localStorage.removeItem('twitter-auth');
            } catch (e) {
                //                 console.error(e);
                setError(e);
            }
        })();
    }, []);

    useEffect(() => {
        if (!wasSuccessful) return;
        setTimeout(() => {
            if (location.href.includes('/authentication/twitter'))
                navigate(
                    `/c/${client.id}/client-configuration/linked-social-accounts`,
                );
        }, 2000);
    }, [wasSuccessful]);

    return (
        <div className="gds-layout__container">
            <div className="gds-layout__row">
                <div
                    className="gds-layout__column--md-12 -m-b-3"
                    style={{ paddingTop: '8rem' }}
                >
                    {!hadError && !wasSuccessful && <Loading />}
                    {wasSuccessful && <Success />}
                    {hadError && <ErrorComponent />}
                </div>
            </div>
        </div>
    );
}

TwitterRedirect.propTypes = {
    client: PropTypes.object.isRequired,
    send: PropTypes.func.isRequired,
};

export default connect(['client'], notificationActions)(TwitterRedirect);
